/* LA GUÍA GUIADA DENTRO DE LA FICHA · clon de FisioVisión.
   El motor (fases, tiempos, cuenta, sonido, láminas) está copiado línea a
   línea de prototipo/paciente.html (6966-7345). Lo único reescrito es el
   pegamento de React: refs en vez de getElementById, y un efecto que limpia
   el reloj al salir. Ni una medida, ni un tono, ni una palabra cambiadas.
   Ver NORMA_CLONAR_NO_INVENTAR.md. */
import React, { useRef, useEffect, useState } from 'react';
import { PROGRAMAS } from './programasGuiados';

export default function ProgramaGuiado({ clave }) {
  const p = PROGRAMAS[clave];
  const caja = useRef(null);
  const PROG = useRef({ reloj:null, fase:0, queda:0, rep:0, parte:0, prep:0, descanso:0, total:0 });
  const AUDIO = useRef(null);
  const SONIDO = useRef(false);
  const LUZ = useRef(null);
  const [abierto, setAbierto] = useState(false);

  /* --- la pantalla no se apaga mientras dura la guía --- */
  const mantenerLuz = () => {
    try {
      if (navigator.wakeLock && !LUZ.current) {
        navigator.wakeLock.request('screen').then(l => { LUZ.current = l; }).catch(() => {});
      }
    } catch (e) {}
  };
  const soltarLuz = () => { try { if (LUZ.current) { LUZ.current.release(); LUZ.current = null; } } catch (e) {} };

  /* ============ EL SONIDO · «pic pic pum» ============
     Sin voz: solo pitidos. Es lo que más ayuda de verdad, porque con el
     sonido NO HACE FALTA MIRAR LA PANTALLA — y el Kegel se hace tumbada
     y con los ojos cerrados. (Idea de Ann, 9-ago-2026.)
     ARRANCA BLOQUEADO a propósito: los navegadores no dejan sonar nada sin
     que la persona lo pida, y nadie quiere que el móvil pite sin avisar. */
  const pitido = (hz, dur, vol) => {
    if (!SONIDO.current || !AUDIO.current) return;
    try {
      const a = AUDIO.current;
      const o = a.createOscillator(), g = a.createGain();
      o.type = 'sine'; o.frequency.value = hz;
      g.gain.setValueAtTime(0, a.currentTime);
      g.gain.linearRampToValueAtTime(vol || .18, a.currentTime + .012);
      g.gain.exponentialRampToValueAtTime(.0001, a.currentTime + (dur || .12));
      o.connect(g); g.connect(a.destination);
      o.start(); o.stop(a.currentTime + (dur || .12) + .02);
    } catch (e) {}
  };
  const vibrar = ms => { try { if (navigator.vibrate) navigator.vibrate(ms); } catch (e) {} };

  const abrirSonido = ev => {
    SONIDO.current = !SONIDO.current;
    if (SONIDO.current) {
      try {
        AUDIO.current = AUDIO.current || new (window.AudioContext || window.webkitAudioContext)();
        if (AUDIO.current.state === 'suspended') AUDIO.current.resume();
        pitido(660, .09, .16);           /* una prueba, para saber que va */
      } catch (e) { SONIDO.current = false; }
    }
    if (caja.current) caja.current.dataset.sonido = SONIDO.current ? 'si' : 'no';
    ev.currentTarget.setAttribute('aria-pressed', SONIDO.current ? 'true' : 'false');
  };

  const sonarFase = tipo => {
    if (tipo === 'in') { pitido(660, .16, .22); vibrar(60); }
    else if (tipo === 'out') { pitido(392, .34, .12); }   /* grave, largo y bajito */
    else if (tipo === 'pausa' || tipo === 'prep') { pitido(440, .08, .10); }
  };
  /* El tic solo suena MIENTRAS SE TRABAJA. En la relajación, silencio:
     un pitido por segundo mientras sueltas es lo contrario de relajar.
     (Ann, 9-ago-2026) */
  const sonarTic = tipo => { if (tipo === 'out' || tipo === 'pausa') return; pitido(880, .035, .055); };
  const sonarFin = () => {
    if (!SONIDO.current || !AUDIO.current) return;
    [523, 659, 784].forEach((hz, i) => setTimeout(() => pitido(hz, .3, .2), i * 160));
    vibrar([80, 60, 80, 60, 160]);
  };

  /* EL RELOJ · el aro se llena DE UNA PIEZA durante toda la fase y la bola
     central encoge al contraer y crece al relajar, como en el encargo de Ann.
     Antes el aro daba un saltito por segundo: con las fases de 1 segundo de
     la parte rápida eso no se veía. */
  const arrancarFase = (segundos, tipo) => {
    PROG.current.total = segundos;
    sonarFase(tipo);
    if (!caja.current) return;
    const aro  = caja.current.querySelector('.fv-prog__aro-l');
    const bola = caja.current.querySelector('.fv-prog__bola');
    if (aro) {
      aro.style.transition = 'none';
      aro.style.strokeDashoffset = '289';
      void aro.getBoundingClientRect();   /* forzar el repintado */
      aro.style.transition = 'stroke-dashoffset ' + segundos + 's linear, stroke .25s ease';
      aro.style.strokeDashoffset = '0';
    }
    if (bola) {
      /* encoge a 0,72 al contraer · crece a 1,15 al relajar */
      const escala = tipo === 'in' ? 0.72 : tipo === 'out' ? 1.15 : 1;
      const dur = Math.min(segundos, 1.2);
      bola.style.transition = 'transform ' + dur + 's cubic-bezier(.3,.7,.4,1), fill .25s ease';
      bola.style.transform = 'scale(' + escala + ')';
    }
  };

  /* LA CUENTA · hacia ARRIBA mientras se trabaja: «uno, dos, tres» es como
     cuenta una persona cuando aguanta algo, y es como lo cuenta la fisio en
     la consulta. Hacia abajo solo en el descanso, que ahí sí es una espera.
     (Ann, 8-ago-2026: «lo del contador hacia atrás no se entiende».) */
  const numeroQueSeVe = (tipo, queda, total) => {
    if (queda <= 0) return '';
    if (tipo === 'pausa') return queda;              /* esperando: 20, 19, 18… */
    return (total - queda + 1);                      /* trabajando: 1, 2, 3… */
  };

  const pintarPrograma = (tit, sub, tipo, queda) => {
    const c = caja.current; if (!c) return;
    const parte = p.partes[PROG.current.parte] || p.partes[0];
    c.querySelector('.fv-prog__fase').textContent = tit;
    c.querySelector('.fv-prog__sub').textContent = sub || '';
    const num = numeroQueSeVe(tipo, queda, PROG.current.total || queda);
    const cta = c.querySelector('.fv-prog__cuenta');
    /* En reposo el reloj NI SE ENSEÑA (se esconde por CSS): un círculo
       vacío con algo dentro no dice nada. Al terminar, el check. (Ann) */
    if (tipo === 'espera' || num === '') {
      cta.innerHTML = (tipo === 'fin')
        ? '<svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12l5 5L20 6"/></svg>'
        : '';
    } else {
      cta.textContent = num;
    }
    const cuantasPartes = p.partes.length;
    c.querySelector('.fv-prog__paso').textContent =
      (tipo === 'espera' || tipo === 'fin') ? ''
        : (cuantasPartes > 1 ? parte.n + ' · ' : '') +
          'repetición ' + Math.min(PROG.current.rep + 1, parte.reps) + ' de ' + parte.reps;
    c.dataset.tipo = tipo;
    /* UN RÓTULO, UN DIBUJO. Mientras pone CONTRAE se ve contraer; mientras
       pone Relaja se ve relajar. Si el dibujo cambia a mitad de fase y el
       texto no, confunde. (Ann, 8-ago-2026) */
    let lam = 'descansar';
    if (tipo === 'in')  lam = 'contraer';
    if (tipo === 'out') lam = 'relajar';
    c.dataset.lamina = lam;
  };

  /* TRES ESTADOS y sus mandos, como pidió Ann:
       parado → Empezar · en marcha → Pausar/Reiniciar/Parar
       en pausa → Continuar/Reiniciar/Parar
     El reloj NO es un botón: los mandos están abajo y se ven. */
  const estadoPrograma = e => { if (caja.current) caja.current.dataset.estado = e; };

  const empezarPrograma = () => {
    mantenerLuz();
    const S = PROG.current;
    S.parte = 0; S.rep = 0; S.fase = 0; S.descanso = 0;
    S.prep = p.prep || 3;
    if (caja.current) caja.current.classList.add('es-andando');
    estadoPrograma('andando');
    arrancarFase(S.prep, 'prep');
    pintarPrograma('Preparados', 'Colócate cómodamente', 'prep', S.prep);
    if (S.reloj) clearInterval(S.reloj);
    S.reloj = setInterval(ticPrograma, 1000);
  };

  const pausarPrograma = () => {
    const S = PROG.current;
    if (!S.reloj) return;
    clearInterval(S.reloj); S.reloj = null;
    if (caja.current) {
      caja.current.classList.remove('es-andando');
      caja.current.querySelector('.fv-prog__sub').textContent = 'En pausa · toca Continuar cuando quieras';
    }
    estadoPrograma('pausa');
    soltarLuz();
  };

  const seguirPrograma = () => {
    const S = PROG.current;
    if (S.reloj) return;
    mantenerLuz();
    if (caja.current) caja.current.classList.add('es-andando');
    estadoPrograma('andando');
    S.reloj = setInterval(ticPrograma, 1000);
  };

  const reiniciarPrograma = () => empezarPrograma();

  const detenerPrograma = () => {
    const S = PROG.current;
    if (S.reloj) { clearInterval(S.reloj); S.reloj = null; }
    if (caja.current) caja.current.classList.remove('es-andando');
    estadoPrograma('parado');
    S.parte = 0; S.rep = 0; S.fase = 0; S.descanso = 0; S.prep = 0;
    /* en reposo, UNA sola frase: la instrucción. Nada más. (Ann, 8-ago-2026) */
    pintarPrograma('', 'Toca empezar para comenzar la rutina', 'espera', 0);
    soltarLuz();
  };

  const acabarPrograma = () => {
    sonarFin();
    const S = PROG.current;
    if (S.reloj) { clearInterval(S.reloj); S.reloj = null; }
    if (caja.current) caja.current.classList.remove('es-andando');
    estadoPrograma('parado');
    pintarPrograma('¡Hecho!', 'Has completado la guía', 'fin', 0);
    soltarLuz();
  };

  function ticPrograma() {
    const S = PROG.current;
    const parte = p.partes[S.parte];

    /* descanso entre partes */
    if (S.descanso > 0) {
      S.descanso--;
      if (S.descanso > 0) {
        pintarPrograma('Descansa', 'Ahora viene: ' + p.partes[S.parte].n, 'pausa', S.descanso);
        return;
      }
      const f0 = p.partes[S.parte].fases[0];
      S.queda = f0.s;
      arrancarFase(S.queda, f0.tipo);
      pintarPrograma(f0.t, f0.sub, f0.tipo, S.queda);
      return;
    }

    if (S.prep > 0) {
      S.prep--;
      if (S.prep > 0) { pintarPrograma('Preparados', 'Colócate cómodamente', 'prep', S.prep); return; }
      S.queda = parte.fases[0].s;
      arrancarFase(S.queda, parte.fases[0].tipo);
      pintarPrograma(parte.fases[0].t, parte.fases[0].sub, parte.fases[0].tipo, S.queda);
      return;
    }

    S.queda--;
    const f = parte.fases[S.fase];
    if (S.queda > 0) {
      sonarTic(f.tipo);
      pintarPrograma(f.t, f.sub, f.tipo, S.queda);
      return;
    }

    /* se acaba la fase: o pasa a la siguiente, o cuenta una repetición */
    S.fase++;
    if (S.fase >= parte.fases.length) {
      S.fase = 0; S.rep++;
      if (S.rep >= parte.reps) {
        S.parte++;
        if (S.parte >= p.partes.length) { acabarPrograma(); return; }
        S.rep = 0; S.fase = 0;
        if (p.descanso) {                       /* 20 s entre partes */
          S.descanso = p.descanso;
          arrancarFase(p.descanso, 'pausa');
          pintarPrograma('Descansa', 'Ahora viene: ' + p.partes[S.parte].n, 'pausa', S.descanso);
          return;
        }
      }
    }
    const sig = parte.fases[S.fase];
    S.queda = sig.s;
    arrancarFase(sig.s, sig.tipo);
    /* un consejo cada dos repeticiones, sin interrumpir */
    let sub = sig.sub;
    if (S.fase === 0 && S.rep > 0 && S.rep % 2 === 0 && p.avisos.length) {
      sub = p.avisos[(S.rep / 2 - 1) % p.avisos.length];
    }
    pintarPrograma(sig.t, sub, sig.tipo, S.queda);
  }

  /* al abrir la tarjeta, el programa nace parado y con su frase */
  useEffect(() => { if (abierto) detenerPrograma(); /* eslint-disable-next-line */ }, [abierto]);
  /* si la paciente se va de la ficha, no se queda un reloj corriendo por detrás */
  useEffect(() => () => {
    if (PROG.current.reloj) clearInterval(PROG.current.reloj);
    soltarLuz();
  }, []);

  if (!p) return null;

  return (
    /* APOYO EN PANTALLA · pequeño a propósito: acompaña al ejercicio,
       no lo sustituye. Se abre aquí mismo, sin cambiar de pantalla. */
    <div className="fv-apoyo">
      <button className="fv-apoyo__abrir" onClick={() => setAbierto(true)}>
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
        Empezar el {p.n}
        <svg className="fv-apoyo__fl" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>
      </button>

      <div className={'fv-prog' + (abierto ? ' on' : '')} ref={caja}>
        <div className="fv-prog__visor">
          <div className="fv-prog__reloj">
            <svg viewBox="0 0 100 100" aria-hidden="true">
              <circle className="fv-prog__aro-f" cx="50" cy="50" r="46"/>
              <circle className="fv-prog__bola"  cx="50" cy="50" r="34"/>
              <circle className="fv-prog__aro-l" cx="50" cy="50" r="46" transform="rotate(-90 50 50)"/>
            </svg>
            <div className="fv-prog__cuenta"></div>
          </div>
          {/* DIBUJO DEL SUELO PÉLVICO · las cuatro láminas de Ann.
              Se cambia la que toca según la fase. Enseña QUÉ pasa dentro,
              que es lo que no consigue ningún texto. */}
          {p.laminas && (
            <div className="fv-suelo-caja">
              <img className="fv-suelo-img es-descansar" src="/fisiovision/kegel-descansar.png" alt="" />
              <img className="fv-suelo-img es-contraer"  src="/fisiovision/kegel-contraer.png"  alt="" />
              <img className="fv-suelo-img es-mantener"  src="/fisiovision/kegel-mantener.png"  alt="" />
              <img className="fv-suelo-img es-relajar"   src="/fisiovision/kegel-relajar.png"   alt="" />
              <span className="fv-suelo__rot">suelo pélvico</span>
            </div>
          )}
        </div>
        <div className="fv-prog__fase"></div>
        <div className="fv-prog__sub">Toca empezar para comenzar la rutina</div>
        <div className="fv-prog__paso"></div>
        <div className="fv-prog__mandos">
          <button className="fv-boton fv-boton--accion fv-prog__ir"     onClick={empezarPrograma}>Empezar</button>
          <button className="fv-boton fv-boton--accion fv-prog__seguir" onClick={seguirPrograma}>Continuar</button>
          <button className="fv-boton fv-boton--suave  fv-prog__pausa"  onClick={pausarPrograma}>Pausar</button>
          <button className="fv-boton fv-boton--suave  fv-prog__reinic" onClick={reiniciarPrograma}>Reiniciar</button>
          <button className="fv-boton fv-boton--parar  fv-prog__stop"   onClick={detenerPrograma}>Parar</button>
          {/* el altavoz · arranca bloqueado */}
          <button className="fv-boton fv-boton--suave fv-boton--icono fv-prog__son" onClick={abrirSonido} aria-pressed="false" aria-label="Sonido">
            <svg className="fv-prog__son-off" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H3v6h3l5 4z"/><path d="M22 9l-6 6M16 9l6 6"/></svg>
            <svg className="fv-prog__son-on"  width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H3v6h3l5 4z"/><path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13"/></svg>
            <span></span>
          </button>
        </div>
      </div>
    </div>
  );
}
